import React, { useEffect, useState } from 'react';
import {
  FaTimes, FaCheck, FaArrowRight, FaClock, FaTag,
  FaUtensils, FaBroom, FaConciergeBell, FaHandsHelping, FaStar
} from 'react-icons/fa';
import { useApp } from '../App';
import './ServiceModal.css';

const ICON_MAP = {
  chef: <FaUtensils />,
  home: <FaBroom />,
  reception: <FaConciergeBell />,
  clean: <FaHandsHelping />
};

/* Map service card icon → the RequestModal's service_type key */
const SERVICE_KEY_MAP = {
  chef: 'Chef',
  home: 'Housekeeper',
  reception: 'Receptionist',
  clean: 'Cleaner'
};

function ServiceModal({ service, onClose }) {
  const { setShowRequest, setPreselectedPro, setPreselectedService } = useApp();
  const [visible, setVisible] = useState(false);

  /* Animate in on mount */
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 20);
    return () => clearTimeout(t);
  }, []);

  /* Close on Escape + lock body scroll */
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 220);
  };

  const handleRequest = () => {
    /* Map icon → service key and preselect it in the RequestModal */
    const mappedService = SERVICE_KEY_MAP[service.icon] || 'Chef';

    if (setPreselectedPro) setPreselectedPro(null);          // clear any old pro
    if (setPreselectedService) setPreselectedService(mappedService);

    setShowRequest(true);
    handleClose();
  };

  if (!service) return null;

  /* Parse features string into array */
  const features = (service.features || '')
    .split('|')
    .map(f => f.trim())
    .filter(Boolean);

  return (
    <div
      className={`svcmodal-root ${visible ? 'svcmodal-show' : ''}`}
      onClick={handleClose}
    >
      <div className="svcmodal-box" onClick={e => e.stopPropagation()}>
        <button className="svcmodal-close" onClick={handleClose} aria-label="Close">
          <FaTimes />
        </button>

        {/* ---------- HERO IMAGE ---------- */}
        <div className="svcmodal-hero">
          {service.image ? (
            <img src={service.image} alt={service.title} className="svcmodal-hero-img" />
          ) : (
            <div className="svcmodal-hero-fallback">{ICON_MAP[service.icon] || <FaStar />}</div>
          )}
          <div className="svcmodal-hero-overlay" />
          <div className="svcmodal-hero-content">
            <span className="svcmodal-icon-badge">
              {ICON_MAP[service.icon] || <FaStar />}
            </span>
            <h2 className="svcmodal-title">{service.title}</h2>
            {service.subtitle && (
              <p className="svcmodal-subtitle">{service.subtitle}</p>
            )}
          </div>
        </div>

        {/* ---------- BODY ---------- */}
        <div className="svcmodal-body">
          {/* Meta chips */}
          {(service.price_from || service.duration_info) && (
            <div className="svcmodal-chips">
              {service.price_from && (
                <span className="svcmodal-chip svcmodal-chip-price">
                  <FaTag /> {service.price_from}
                </span>
              )}
              {service.duration_info && (
                <span className="svcmodal-chip svcmodal-chip-duration">
                  <FaClock /> {service.duration_info}
                </span>
              )}
            </div>
          )}

          {/* Long description */}
          {(service.long_description || service.description) && (
            <div className="svcmodal-section">
              <h3>About This Service</h3>
              <p>{service.long_description || service.description}</p>
            </div>
          )}

          {/* Features list */}
          {features.length > 0 && (
            <div className="svcmodal-section">
              <h3>What's Included</h3>
              <ul className="svcmodal-features">
                {features.map((f, i) => (
                  <li key={i}>
                    <span className="svcmodal-check"><FaCheck /></span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* CTA */}
          <div className="svcmodal-cta-wrap">
            <button className="svcmodal-cta" onClick={handleRequest}>
              {service.cta_label || 'Request This Service'} <FaArrowRight />
            </button>
            <button className="svcmodal-cta-outline" onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ServiceModal;
